/**
 * Recurring-session series helpers.
 *
 * Sessions are created one POST at a time, so nothing on the server ties the
 * occurrences of one "repeat" together. The calendar mints a series_id up
 * front and stamps it on every occurrence; that shared key is what the
 * backend expands when an edit or delete is scoped to the series.
 */

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Mint a series key.
 * crypto.randomUUID needs a secure context, which localhost and https both
 * are — the manual fallback only matters for plain-http staging hosts.
 * @returns {string} UUID
 */
export const newSeriesId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * Describe a recurrence in the words a user would read back, e.g.
 * "Weekly on Tue", "Every 2 weeks on Mon, Wed", "Every weekday".
 *
 * Stored on each occurrence for display only — the rows themselves remain the
 * source of truth for when the sessions actually are.
 *
 * @param {Object} opts
 * @param {string} opts.recurrence - 'daily' | 'weekly' | 'monthly' | 'weekdays' | 'custom'
 * @param {Date} [opts.startDate] - anchor date, used to name the weekday
 * @param {number} [opts.interval=1] - repeat every N units (custom only)
 * @param {string} [opts.unit='week'] - 'day' | 'week' | 'month' (custom only)
 * @param {number[]} [opts.customDays=[]] - day-of-week numbers (custom only)
 * @returns {string|null} Descriptor, or null when there is no recurrence
 */
export const describeRecurrence = ({ recurrence, startDate, interval = 1, unit = 'week', customDays = [] } = {}) => {
  if (!recurrence || recurrence === 'none') return null;

  const weekdayOf = (d) => (d instanceof Date && !isNaN(d) ? WEEKDAY_LABELS[d.getDay()] : null);

  switch (recurrence) {
    case 'daily':
      return 'Daily';
    case 'weekdays':
      return 'Every weekday (Mon–Fri)';
    case 'weekly': {
      const day = weekdayOf(startDate);
      return day ? `Weekly on ${day}` : 'Weekly';
    }
    case 'monthly': {
      const dayNum = startDate instanceof Date && !isNaN(startDate) ? startDate.getDate() : null;
      return dayNum ? `Monthly on day ${dayNum}` : 'Monthly';
    }
    case 'custom': {
      const every = interval > 1 ? `Every ${interval} ${unit}s` : `Every ${unit}`;
      if (unit === 'week' && customDays.length > 0) {
        const days = [...customDays].sort((a, b) => a - b).map((d) => WEEKDAY_LABELS[d]);
        return `${every} on ${days.join(', ')}`;
      }
      return every;
    }
    default:
      return null;
  }
};

/**
 * Truncate a descriptor to the column width so a long custom rule can't fail
 * the insert on the 255-char varchar.
 * @param {string|null} rule
 * @returns {string|null}
 */
export const clampRecurrenceRule = (rule) => (rule ? String(rule).slice(0, 255) : null);
